<template>
  <div class="product-page" ref="pageContainer">
    <!-- 頁面標題區 -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">
            {{ title }}
          </h1>
        </div>
      </div>
    </section>

    <!-- 類別過濾導航區 -->
    <section class="filter-section">
      <div class="container">
        <!-- 搜尋框區塊 -->
        <div class="search-section">
          <div class="search-box">
            <i class="fas fa-search search-icon"></i>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="搜尋商品名稱或描述..."
              class="search-input"
              @input="handleSearch"
            />
            <button
              v-if="searchQuery"
              class="clear-search-btn"
              @click="clearSearch"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <!-- 手機版下拉選單 (移到上方) -->
        <div class="category-filter-mobile">
          <div class="category-dropdown-wrapper">
            <div
              class="category-dropdown-toggle"
              @click="toggleDropdown"
              :class="{ active: showDropdown }"
            >
              <span>{{ getCurrentCategoryName() }}</span>
              <i class="fas fa-chevron-down dropdown-icon"></i>
            </div>
            <div class="category-dropdown-menu" :class="{ show: showDropdown }">
              <div
                v-for="category in categories"
                :key="'mobile-' + category.id"
                class="dropdown-item"
                :class="{ active: selectedMainCategory === category.id }"
                @click="selectCategory(category.id)"
              >
                {{ category.name }}
              </div>
            </div>
          </div>
        </div>

        <!-- 主分類導航 (移到下方) -->
        <div class="category-nav" :data-selected="selectedMainCategory">
          <div class="main-categories" id="main-categories">
            <button
              v-for="category in categories"
              :key="category.id"
              :class="[
                'main-category-btn',
                { active: selectedMainCategory === category.id },
              ]"
              @click="selectMainCategory(category.id)"
            >
              {{ category.name }}
            </button>
          </div>

          <!-- 子分類導航 (只在有子分類且不是全部商品時顯示) -->
          <div
            class="sub-categories"
            :class="{
              show:
                currentSubCategories.length > 0 &&
                selectedMainCategory !== 'all',
            }"
          >
            <button
              v-for="subCategory in currentSubCategories"
              :key="subCategory.id"
              :class="[
                'sub-category-btn',
                { active: selectedSubCategory === subCategory.id },
              ]"
              @click="selectSubCategory(subCategory.id)"
            >
              {{ subCategory.name }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- 商品列表 -->
    <section class="product-section">
      <div class="container">
        <div class="product-grid">
          <div
            v-for="product in paginatedProducts"
            :key="product.id"
            class="product-card"
          >
            <div
              class="product-image-wrapper"
              @click="openProductModal(product)"
            >
              <div class="product-image">
                <!-- 有圖片時顯示圖片 -->
                <img
                  v-if="product.imageUrl"
                  :src="product.imageUrl"
                  :alt="product.name"
                  class="product-img"
                  @error="handleImageError($event, product)"
                />
                <!-- 沒有圖片時顯示佔位符 -->
                <div
                  v-else
                  class="product-placeholder"
                  :style="{
                    background:
                      product.gradient ||
                      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '64px',
                    height: '220px',
                    color: 'white',
                  }"
                >
                  {{ product.emoji || "📦" }}
                </div>
              </div>

                <!-- 商品 Tags（左下角） -->
              <div class="product-tags">
                <span v-if="product.Tag1" class="tag tag-primary">
                  {{ product.Tag1 }}
                </span>
                <span v-if="product.Tag2" class="tag tag-secondary">
                  {{ product.Tag2 }}
                </span>
                <span v-if="product.Tag3" class="tag tag-accent">
                  {{ product.Tag3 }}
                </span>
              </div>
              <div class="product-overlay">
                <span class="view-details">查看詳情</span>
              </div>
              <div
                class="stock-label"
                :class="{ 'no-stock': product.stock === 0 }"
              >
                <span v-if="product.meter === 1">
                  <i class="fas fa-box"></i> {{ product.stock }}
                </span>
                <span v-else-if="product.meter === 0 && product.stock === 0">
                  <i class="fas fa-times-circle"></i> 無庫存
                </span>
                <span v-else-if="product.meter === 0 && product.stock > 0">
                  <i class="fas fa-check-circle"></i> 有庫存
                </span>
              </div>
            </div>
            <div class="product-info">
              <div class="product-category">
                {{ getCategoryName(product.category) }}
              </div>
              <h3 class="product-name">{{ product.name }}</h3>
              <p class="product-description">
                {{ truncateDescription(product.description) }}
              </p>
              <div class="product-footer">
                <p class="product-price">
                  {{ product.price }}
                </p>
                <button
                  class="add-to-cart-btn"
                  @click.stop="openProductModal(product)"
                  :disabled="product.stock === 0"
                  :class="{ disabled: product.stock === 0 }"
                  style="font-size: 30px"
                >
                  <span>+</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 分頁控制 -->
        <div class="pagination-container" v-if="filteredProducts.length > 0">
          <div class="pagination">
            <button
              class="page-btn prev"
              @click="prevPage"
              :disabled="currentPage === 1"
              :class="{ disabled: currentPage === 1 }"
            >
              <i class="fas fa-chevron-left"></i>
            </button>

            <div class="page-numbers">
              <button
                v-for="page in displayedPages"
                :key="page"
                @click="changePage(page)"
                class="page-number"
                :class="{ active: currentPage === page }"
              >
                {{ page }}
              </button>
            </div>

            <button
              class="page-btn next"
              @click="nextPage"
              :disabled="currentPage === totalPages"
              :class="{ disabled: currentPage === totalPages }"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>

          <!-- <div class="page-info">
            顯示 {{ paginatedProducts.length }} 件商品，共
            {{ filteredProducts.length }} 件
          </div> -->
        </div>
      </div>
    </section>

    <!-- 空數據提示 -->
    <div v-if="filteredProducts.length === 0" class="empty-results">
      <div class="container">
        <i class="fas fa-search"></i>
        <p>沒有找到相關產品</p>
        <button class="reset-btn" @click="resetFilter">重置篩選</button>
      </div>
    </div>

    <!-- 模態框 -->
    <transition name="fade">
      <div
        v-if="showModal"
        class="modal-overlay"
        @click.self="closeProductModal"
      >
        <div class="modal-content">
          <button class="modal-close" @click="closeProductModal">
            <i class="fas fa-times"></i>
          </button>
          <div class="modal-body">
            <div class="modal-image-wrapper">
              <img
                :src="selectedProduct.imageUrl"
                :alt="selectedProduct.name"
                class="modal-image"
                @error="handleImageError($event, selectedProduct)"
              />

              <!-- 在模態框圖片上也顯示 Tags -->
              <div class="modal-tags">
                <span v-if="selectedProduct.Tag1" class="tag tag-primary">
                  {{ selectedProduct.Tag1 }}
                </span>
                <span v-if="selectedProduct.Tag2" class="tag tag-secondary">
                  {{ selectedProduct.Tag2 }}
                </span>
                <span v-if="selectedProduct.Tag3" class="tag tag-accent">
                  {{ selectedProduct.Tag3 }}
                </span>
              </div>
            </div>

            <div class="modal-details">
              <div class="modal-header">
                <h2 class="modal-title">{{ selectedProduct.name }}</h2>
                <p class="modal-price">
                  {{ displayPrice }}
                </p>
              </div>
              <!-- 🟦 規格選擇區塊 (如果有多規格才會顯示) -->
              <div
                v-if="
                  selectedProduct.variants &&
                  selectedProduct.variants.length > 0
                "
                class="variant-section"
              >
                <h3 class="variant-title">選擇規格</h3>

                <div class="variant-list">
                  <button
                    v-for="(v, index) in selectedProduct.variants"
                    :key="v.Id || index"
                    class="variant-btn"
                    :class="{ active: selectedVariantIndex === index }"
                    @click="selectVariant(index)"
                  >
                    {{ v.VariantName }}
                  </button>
                </div>
              </div>

              <!-- 🟦 數量選擇 -->
              <div class="quantity-section">
                <h3 class="quantity-title">選擇數量</h3>

                <div class="quantity-box">
                  <button
                    class="qty-btn"
                    @click="decreaseQty"
                    :disabled="buyQuantity <= 1"
                  >
                    −
                  </button>

                  <input
                    class="qty-input"
                    type="number"
                    v-model.number="buyQuantity"
                    @input="validateQty"
                  />

                  <button class="qty-btn" @click="increaseQty">+</button>
                </div>

                <p class="stock-info">
                  <span v-if="selectedProduct.meter === 1">
                    可購入數量：{{
                      currentVariant
                        ? currentVariant.Stock
                        : selectedProduct.stock
                    }}
                    件
                  </span>

                  <span v-else> 可自由選擇數量 </span>
                </p>
              </div>

              <div
                class="modal-stock"
                :class="{ 'no-stock': selectedProduct.stock === 0 }"
              >
                <i
                  v-if="selectedProduct.stock === 0"
                  class="fas fa-times-circle"
                ></i>
                <i v-else class="fas fa-check-circle"></i>
                <span v-if="selectedProduct.meter === 1"
                  >庫存：{{
                    currentVariant
                      ? currentVariant.Stock
                      : selectedProduct.stock
                  }}
                  件</span
                >
                <span v-else> 有庫存 </span>
              </div>

              <div class="modal-section">
                <h3>產品詳情</h3>
                <p class="modal-description">
                  {{ selectedProduct.description }}
                </p>
              </div>
              <div v-if="selectedProduct.videoUrl" class="modal-section">
                <h3>相關影片</h3>
                <a
                  :href="selectedProduct.videoUrl"
                  target="_blank"
                  class="video-link"
                >
                  <i class="fas fa-play-circle"></i>
                  觀看產品介紹影片
                </a>
              </div>

              <div class="modal-actions">
                <button
                  class="btn-primary"
                  :disabled="selectedProduct.stock === 0"
                  :class="{ 'btn-disabled': selectedProduct.stock === 0 }"
                  @click="addToCart(selectedProduct)"
                >
                  <i class="fas fa-shopping-cart"></i> 加入購物車
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 浮動購物車按鈕 -->
    <div
      ref="floatingCart"
      class="floating-cart-btn"
      @click="goToCart"
      :class="{ 'cart-animation': isCartAnimating }"
    >
      <div class="cart-icon">
        <i class="fas fa-shopping-cart"></i>
        <span v-if="cartItemCount > 0" class="cart-badge">{{
          cartItemCount
        }}</span>
      </div>
    </div>
  </div>
</template>

<script src="@/assets/js/products.js" defer></script>
<style src="@/assets/css/products.css"></style>
